import { Component, ElementRef, HostListener, Inject, Input, ViewChild } from '@angular/core';

import { VideogularComponent } from '../Videogular.Component';
import { VG_VOLUME_COMPONENT_TOKEN } from './VgVolumeComponentToken';

/**
 * Component to display a vertical volume bar to control the volume.
 * This must be inside vg-volume directive and requires vg-mute-button to be displayed.
 *
 * <pre>
 * <videogular>
 *    <vg-media></vg-media>
 *
 *    <vg-controls>
 *        <vg-volume>
 *            <vg-mute-button><vg-mute-button>
 *            <vg-volume-bar><vg-volume-bar>
 *        </vg-volume>
 *    </vg-controls>
 * </videogular>
 * </pre>
 *
 */
@Component({
	selector: 'vg-volume-bar',
	host: {
		'[class.smallSizePlayer]': 'isSmallSizePlayer'
	},
	template: `
		<div
			#volumeBar
			class="volumeBar"
			vbControlBarFocus
			tabindex="0"
			role="slider"
			[attr.aria-label]="translations.volumeLevel"
			[attr.aria-valuenow]="volumePercent"
			aria-valuemin="0"
			aria-valuemax="100"
			aria-orientation="horizontal"
			[attr.aria-valuetext]="volumePercent + '%'"
			[tooltip]="volumeTooltip"
			[placement]="'top'"
			[containerClass]="'.tooltipContainer'"
			(click)="onClick($event)"
			(mousedown)="onMouseDown($event)"
			(keydown)="vgVolume.onVolumeAreaKeyDown($event)">
			<div [ngClass]="[
					'volumeBackground',
					isDragging ? 'dragging' : ''
				]">
				<div class="volumeValue" [style.width]="(getVolume() * (100-15)) + '%'"></div>
				<div class="volumeKnob" [style.left]="(getVolume() * (100-15)) + '%'"></div>
			</div>
		</div>
		<ng-template #volumeTooltip>
			{{ volumePercent + '%'}}
		</ng-template>
	`,
	standalone: false,
	styleUrls: ['./VgVolumeBar.Component.ng.less']
})
export class VgVolumeBarComponent {
	@Input() public translations: any;
	@Input() public isSmallSizePlayer: boolean;

	@ViewChild('volumeBar', { static: true })
	private volumeBarRef: ElementRef;

	private elem: HTMLElement;
	public isDragging: boolean;
	private mouseDownPosX: number;

	constructor(
		ref: ElementRef,
		@Inject(VG_VOLUME_COMPONENT_TOKEN)
		public vgVolume: any,
		private API: VideogularComponent
	) {
		this.elem = ref.nativeElement;
		this.isDragging = false;
	}

	public onClick(event: { clientX: number }) {
		this.setVolume(this.calculateVolume(event.clientX));
	}

	public get volumePercent(): number {
		return Math.ceil(this.getVolume() * 100);
	}

	public onMouseDown(event: MouseEvent): void {
		if(event.button !== 0) {
			return;
		}

		this.mouseDownPosX = event.clientX;
		this.isDragging = true;
	}

	@HostListener('document:mousemove', ['$event'])
	public onDrag(event: { clientX: number }): void {
		if (this.isDragging) {
			this.setVolume(this.calculateVolume(event.clientX));
		}
	}

	@HostListener('document:mouseup', ['$event'])
	public onStopDrag(event: { clientX: number }): void {
		if (this.isDragging) {
			this.isDragging = false;
			if (this.mouseDownPosX === event.clientX) {
				this.setVolume(this.calculateVolume(event.clientX));
			}
		}
	}

	public calculateVolume(mousePosX: number): number {
		const recObj = this.volumeBarRef.nativeElement.getBoundingClientRect();
		const volumeBarOffsetLeft: number = recObj.left;
		const volumeBarWidth: number = recObj.width;
		return (mousePosX - volumeBarOffsetLeft) / volumeBarWidth;
	}

	public setVolume(vol: number): void {
		//Todo - Fix later. Some minor issue with width calulation.
		vol = vol > 0.95 ? 1 : vol;
		this.API.setVolume(vol);
		this.vgVolume.storeVolumePreference();
	}

	public getVolume(): number {
		return this.API.volume;
	}
}
